import { useCallback, useEffect, useRef, useState } from 'react';
import { GeoMeasurement } from './geoApi';

/**
 * Замер местоположения браузером. Первый ответ браузера обычно грубый (по Wi-Fi или IP), дальше точность растет,
 * поэтому замер идет до MEASURE_MS и запоминает лучший ответ; при точности ENOUGH_M замер заканчивается раньше.
 */
export const MEASURE_MS = 25000;
const ENOUGH_M = 8;
const TICK_MS = 500;

/**
 * measuring - идет замер; done - есть лучший замер (best); denied - пользователь запретил доступ;
 * unavailable - устройство не определило место; insecure - страница не на https, браузер не дает координаты;
 * unsupported - в браузере нет геолокации
 */
export type MeasureStatus = 'idle' | 'measuring' | 'done' | 'denied' | 'unavailable' | 'insecure' | 'unsupported';

export interface GeoMeasureState {
  status: MeasureStatus;
  best: GeoMeasurement | null;
  samples: number;
  elapsedMs: number;
}

const IDLE: GeoMeasureState = { status: 'idle', best: null, samples: 0, elapsedMs: 0 };

export function useGeoMeasure(autoStart: boolean): { state: GeoMeasureState; start: () => void; stop: () => void } {
  const [state, setState] = useState<GeoMeasureState>(IDLE);
  const watchRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);

  const clear = useCallback(() => {
    if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    watchRef.current = null;
    timerRef.current = null;
  }, []);

  /** Закончить замер: без явного статуса - done, если был хоть один ответ */
  const finish = useCallback((status?: MeasureStatus) => {
    clear();
    setState(prev => ({ ...prev, status: status || (prev.best ? 'done' : 'unavailable') }));
  }, [clear]);

  const start = useCallback(() => {
    clear();
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setState({ ...IDLE, status: 'unsupported' });
      return;
    }
    if (window.isSecureContext === false) {
      setState({ ...IDLE, status: 'insecure' });
      return;
    }
    startedAtRef.current = Date.now();
    setState({ ...IDLE, status: 'measuring' });
    watchRef.current = navigator.geolocation.watchPosition(
      position => {
        const sample = { latitude: position.coords.latitude, longitude: position.coords.longitude, accuracy: position.coords.accuracy };
        setState(prev => ({
          ...prev,
          best: !prev.best || sample.accuracy <= prev.best.accuracy ? sample : prev.best,
          samples: prev.samples + 1
        }));
        if (sample.accuracy <= ENOUGH_M) finish('done');
      },
      error => {
        if (error.code === error.PERMISSION_DENIED) finish('denied');
        else if (error.code === error.POSITION_UNAVAILABLE) finish();
        // TIMEOUT: браузер повторит сам, замер закончит общий таймер
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: MEASURE_MS }
    );
    timerRef.current = window.setInterval(() => {
      const elapsedMs = Date.now() - startedAtRef.current;
      if (elapsedMs >= MEASURE_MS) finish();
      else setState(prev => ({ ...prev, elapsedMs }));
    }, TICK_MS);
  }, [clear, finish]);

  /** Остановить досрочно (переход к карте во время замера): лучший замер сохраняется */
  const stop = useCallback(() => {
    if (watchRef.current !== null || timerRef.current !== null) finish();
  }, [finish]);

  useEffect(() => {
    if (autoStart) start();
    return clear;
  }, [autoStart, start, clear]);

  return { state, start, stop };
}
